import { useId, type ReactNode, type Ref } from 'react';
import { ChevronDown, PanelLeftClose } from 'lucide-react';
import { XIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Checkbox } from '@voll-intelligence/ui-kit/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@voll-intelligence/ui-kit/ui/collapsible';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@voll-intelligence/ui-kit/ui/input-group';
import { RadioGroup, RadioGroupItem } from '@voll-intelligence/ui-kit/ui/radio-group';
import { Slider } from '@voll-intelligence/ui-kit/ui/slider';
import { Switch } from '@voll-intelligence/ui-kit/ui/switch';
import './results_filters_panel.css';

type ResultsFiltersPanelCopy = {
  filtersLabel: string;
  collapseFiltersLabel: string;
  /** O fechar da folha de tela cheia do palco estreito (`dismiss='close'`) —
   *  o X do cabeçalho, no lugar do recolher da coluna (#2663). */
  closeFiltersLabel: string;
  resetFiltersLabel: string;
};

/**
 * O cabeçalho fixo (recolher/fechar + `Filtros` + `Redefinir`) é UM contrato:
 * com ele (o padrão) o primeiro botão precisa de destino, então `onCollapse` é
 * obrigatório; `header: false` é para quando a casca de fora já o desenha (a
 * coluna do `ResultsLayout`, que tem heading e Redefinir próprios — dois
 * cabeçalhos iguais a 8px um do outro), e aí não há botão a ligar: recolher,
 * glifo e ref ficam de fora por tipo, não por convenção.
 */
export type ResultsFiltersPanelHeader =
  | {
      header?: true;
      /** O que o primeiro botão do cabeçalho FAZ com o painel: `collapse` (o
       *  padrão) é o recolher da coluna — glifo de painel, e a coluna vira o
       *  rail; `close` é o fechar da folha de tela cheia do palco estreito —
       *  um X, e a folha vai embora (#2663). O gesto sai pelo mesmo
       *  `onCollapse`: quem monta o painel sabe o que ele é; o painel só
       *  troca o glifo e o nome. */
      dismiss?: 'collapse' | 'close';
      collapseButtonRef?: Ref<HTMLButtonElement>;
      onCollapse(): void;
    }
  | { header: false; dismiss?: never; collapseButtonRef?: never; onCollapse?: never };

export type ResultsFiltersPanelProps = ResultsFiltersPanelHeader & {
  copy: ResultsFiltersPanelCopy;
  /** Há corte ativo? É o que liga o `Redefinir todos` do cabeçalho. */
  active: boolean;
  locked?: boolean;
  /** A casca de vidro (fundo, anel, sombra, blur). `false` na página
   *  determinística do resultado, onde os filtros são uma coluna DA página —
   *  painel de vidro dentro de página lê como caixa dentro de caixa (a mesma
   *  prop `moldura` do protótipo). Na tela cheia (o padrão) a coluna flutua
   *  sobre o mapa e a moldura é o que a descola do tile. */
  frame?: boolean;
  /** O corpo rolável: as seções de corte do produto, separadas por `Separator`. */
  children: ReactNode;
  /** O pé fixo do painel (o composer de comando do hotel). */
  footer?: ReactNode;
  onReset(): void;
};

/**
 * A coluna de cortes das páginas de resultados — a anatomia dos cortes do
 * aéreo, por decisão do protótipo (`CortesDoHotel`): cabeçalho fixo (recolher +
 * `Filtros` + `Redefinir todos`, desabilitado sem corte), corpo com scroll
 * próprio e um pé opcional. `frame=false` é a variante de coluna DE página (sem
 * a casca de vidro da tela cheia). As seções são do produto; a casca é uma.
 */
export function ResultsFiltersPanel({
  copy,
  active,
  locked = false,
  frame = true,
  dismiss = 'collapse',
  header = true,
  collapseButtonRef,
  children,
  footer,
  onReset,
  onCollapse,
}: Readonly<ResultsFiltersPanelProps>): ReactNode {
  // Sem cabeçalho o painel é o CORPO de uma coluna que já é o `<aside>`
  // nomeado (o `ResultsLayout`): outro marco com o mesmo nome dentro dele
  // duplicaria a região para quem navega por marcos.
  const Root = header ? 'aside' : 'div';
  return (
    <Root
      className="iuk-results-filters"
      data-frame={frame ? undefined : 'false'}
      // Sem cabeçalho é a coluna do `ResultsLayout`: os eixos alinham pelo heading
      // dela (#4580). A folha do palco estreito tem cabeçalho e mantém o respiro.
      data-layout={header ? undefined : 'column'}
      {...(header ? { 'aria-label': copy.filtersLabel } : {})}
    >
      {header && (
        <header>
          <Button
            ref={collapseButtonRef}
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={dismiss === 'close' ? copy.closeFiltersLabel : copy.collapseFiltersLabel}
            onClick={onCollapse}
          >
            {dismiss === 'close' ? (
              <XIcon aria-hidden="true" />
            ) : (
              <PanelLeftClose aria-hidden="true" />
            )}
          </Button>
          <h2 className="text-title">{copy.filtersLabel}</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-mr-2 rounded-full"
            disabled={locked || !active}
            onClick={onReset}
          >
            {copy.resetFiltersLabel}
          </Button>
        </header>
      )}
      <div className="iuk-results-filter-body scrollbar-rail">{children}</div>
      {footer}
    </Root>
  );
}

/**
 * Rótulo do eixo por fora, controle dentro — o `Bloco` do protótipo
 * (`bancada_de_voos/cortes.tsx`): a linha do rótulo inteira é o gatilho de
 * colapso, com o chevron na ponta dizendo o estado (aberto = pra cima). Nasce
 * aberto — recolher é gesto de quem quer encurtar a coluna, não o padrão.
 */
export function FilterSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <Collapsible defaultOpen className="iuk-results-filter-section grid gap-3">
      <CollapsibleTrigger className="group/section -mx-1 flex items-center justify-between gap-2 rounded-md px-1 text-left">
        <span className="text-ui font-medium">{title}</span>
        <ChevronDown
          aria-hidden="true"
          className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[panel-open]/section:rotate-180"
        />
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}

/** Interruptor com rótulo — a linha inteira é o alvo (label embrulha o switch),
 *  o `SwitchDoCorte` do protótipo: o corte é UMA pergunta sim/não.
 *
 *  O nome acessível vai por `aria-labelledby`, não pelo `<label>` em volta: o
 *  Base UI desenha o switch como `<span role="switch">`, que não é elemento
 *  rotulável — o `label` só dá nome ao `<input>` escondido dele, e o controle
 *  que o leitor de tela anuncia ficava sem nome (#3217 C10). */
export function SwitchFilterRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onChange: (checked: boolean) => void;
}): ReactNode {
  const labelId = useId();
  return (
    <label className="text-detail flex cursor-pointer items-center gap-2.5 py-0.5 font-medium">
      <Switch
        checked={checked}
        disabled={disabled}
        aria-labelledby={labelId}
        onCheckedChange={onChange}
      />
      <span id={labelId}>{label}</span>
    </label>
  );
}

/** A contagem na ponta da linha — quantos resultados sobram com este corte,
 *  ignorando o próprio eixo (senão o número mudaria de significado no gesto). */
function OptionCount({ count }: { readonly count: number }): ReactNode {
  return (
    <span className="iuk-results-filter-count text-caption text-muted-foreground tabular-nums">
      {count}
    </span>
  );
}

const OPTION_ROW_CLASS =
  'text-detail flex cursor-pointer items-center gap-2.5 px-1 py-2.5 text-left disabled:cursor-default disabled:opacity-50';

/** Linha de rádio empilhada — o átomo do eixo de escolha ÚNICA (Nota e
 *  Distância), na anatomia do protótipo (`LinhaDeRadio`): o alvo é a linha
 *  inteira, e reclicar a marcada volta pro neutro — semântica de TOGGLE
 *  (`aria-pressed`), que rádio de verdade não tem. O círculo do DS é só o
 *  INDICADOR visual, fora da árvore de acessibilidade (`aria-hidden`): um
 *  controle por linha, sem `role="radio"` duplicando o botão. */
function RadioFilterRow({
  value,
  label,
  count,
  selected,
  disabled,
  zeroSelectable,
  onPick,
}: {
  readonly value: string;
  readonly label: string;
  /** Ausente = eixo sem contagem honesta (lista parcial num corte de servidor,
   *  #3399 c): a linha sai sem número e nunca tranca em zero. */
  readonly count?: number;
  readonly selected: boolean;
  readonly disabled?: boolean;
  /** Lista parcial + eixo de corte no servidor: zero local não prova ausência
   *  no conjunto, então a linha zerada segue clicável (ver `countsPartial`). */
  readonly zeroSelectable?: boolean;
  readonly onPick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={OPTION_ROW_CLASS}
      aria-pressed={selected}
      /* Contagem zero fica VISÍVEL e inerte, como no protótipo (`LinhaDeRadio`,
         `disabled={custo.n === 0}`): sumir com a opção mudaria a altura da
         coluna a cada clique, e deixá-la clicável leva a uma lista vazia sem
         dizer por quê — VÁLIDO quando a contagem é exaustiva. Com a lista
         parcial e o corte indo ao servidor, `zeroSelectable` relaxa a trava. */
      disabled={disabled || (count === 0 && !zeroSelectable)}
      onClick={onPick}
    >
      <RadioGroupItem
        value={value}
        aria-hidden="true"
        tabIndex={-1}
        className="pointer-events-none"
      />
      <span className="min-w-0 flex-1">{label}</span>
      {count !== undefined && <OptionCount count={count} />}
    </button>
  );
}

/** Linha marcável da checklist — o alvo é a linha inteira (`ListaSimples` do
 *  protótipo) e o estado mora no botão (`aria-pressed`); a caixa do DS é só o
 *  INDICADOR visual, fora da árvore de acessibilidade (`aria-hidden`) — sem um
 *  segundo `role="checkbox"` dentro do controle. Na ponta, a CONTAGEM de quem
 *  sobra com o corte ou, quando o eixo não conta (a categoria do carro, que
 *  mostra o preço "a partir de"), um `detail` já em prosa. */
export function CheckFilterRow({
  label,
  glyph,
  checked,
  count,
  detail,
  disabled,
  zeroSelectable,
  onToggle,
}: {
  readonly label: string;
  /** O glifo da opção, já `aria-hidden` (o vocabulário é do produto). */
  readonly glyph?: ReactNode;
  readonly checked: boolean;
  readonly count?: number;
  /** A leitura da ponta quando não há contagem ("R$ 450"). */
  readonly detail?: string;
  readonly disabled?: boolean;
  /** Ver `RadioFilterRow.zeroSelectable` — mesmo relaxamento, mesmo motivo. */
  readonly zeroSelectable?: boolean;
  readonly onToggle: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={OPTION_ROW_CLASS}
      aria-pressed={checked}
      /* Zero-em-linha-MARCADA não tranca: a opção ligada precisa continuar
         acionável para desfazer o próprio corte (o princípio do interruptor de
         política) — outro eixo pode zerar a faixa marcada, e desabilitar aqui
         prenderia o filtro. Desmarcada, zero segue inerte e visível. */
      disabled={disabled || (count === 0 && !checked && !zeroSelectable)}
      onClick={onToggle}
    >
      <Checkbox
        checked={checked}
        aria-hidden="true"
        tabIndex={-1}
        className="pointer-events-none"
      />
      {glyph}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && <OptionCount count={count} />}
      {detail !== undefined && (
        <span className="text-caption text-muted-foreground tabular-nums">{detail}</span>
      )}
    </button>
  );
}

/** Eixo de escolha ÚNICA empilhado (Nota e Distância do hotel) — linhas de
 *  rádio com a contagem na ponta; reclicar a marcada volta pro neutro. */
export function SingleChoiceSection({
  title,
  options,
  current,
  locked,
  labelOf,
  countOf,
  zeroSelectable,
  onPick,
}: {
  readonly title: string;
  readonly options: readonly number[];
  readonly current: number | undefined;
  readonly locked?: boolean;
  readonly labelOf: (option: number) => string;
  readonly countOf: (option: number) => number | undefined;
  readonly zeroSelectable?: boolean;
  readonly onPick: (option: number | undefined) => void;
}): ReactNode {
  return (
    <FilterSection title={title}>
      {/* O `RadioGroup` aqui é SÓ o carregador de estado dos indicadores
          visuais (os círculos, `aria-hidden` nas linhas): quem responde por
          foco, clique e estado é o botão de cada linha — por isso o grupo sai
          da árvore de acessibilidade (`role="presentation"`), em vez de
          anunciar um radiogroup sem rádios. */}
      <RadioGroup
        role="presentation"
        value={current === undefined ? '' : String(current)}
        className="-mx-1 w-auto gap-0"
      >
        {options.map((option) => (
          <RadioFilterRow
            key={option}
            value={String(option)}
            label={labelOf(option)}
            count={countOf(option)}
            selected={current === option}
            disabled={locked}
            zeroSelectable={zeroSelectable}
            onPick={() => {
              onPick(current === option ? undefined : option);
            }}
          />
        ))}
      </RadioGroup>
    </FilterSection>
  );
}

function PriceField({
  amount,
  symbol,
  label,
  disabled,
  onCommit,
}: {
  readonly amount: number;
  readonly symbol: string;
  readonly label: string;
  readonly disabled?: boolean;
  readonly onCommit: (amount: number) => void;
}): ReactNode {
  return (
    <InputGroup className="w-24">
      <InputGroupAddon>
        <InputGroupText>{symbol}</InputGroupText>
      </InputGroupAddon>
      <InputGroupInput
        key={amount}
        inputMode="numeric"
        defaultValue={String(Math.round(amount))}
        aria-label={label}
        className="tabular-nums"
        disabled={disabled}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        onBlur={(event) => {
          const digits = event.currentTarget.value.replace(/\D/g, '');
          const next = Number(digits);
          // Sair sem mudar o número não é um corte novo: o campo pode mostrar
          // uma ponta fora da régua, e o encaixe do commit a reescreveria.
          if (digits !== '' && Number.isFinite(next) && next !== Math.round(amount)) onCommit(next);
        }}
      />
    </InputGroup>
  );
}

/** O símbolo da moeda da lista ("R$", "US$") — formato, não copy: sai do código
 *  ISO que a lista já carrega, pelo mesmo Intl que formata os preços dela. */
function currencySymbol(currency: string): string {
  try {
    return (
      new Intl.NumberFormat(undefined, { style: 'currency', currency })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? currency
    );
  } catch {
    return currency;
  }
}

/** Passo da faixa de preço, na moeda da lista (o protótipo usa `10_00` em
 *  centavos). Sem passo o slider anda de 1 em 1 e a alça vira agulha. */
const PRICE_STEP = 10;

/** As pontas da régua ARREDONDADAS para fora, ao passo: o slider anda de `min`
 *  em múltiplos de `step`, então com piso 277 e teto 555 a alça parava em 547
 *  enquanto o campo dizia 555 — o teto era inalcançável (#3217 C9). Régua de
 *  270–560 cobre o catálogo inteiro e cada degrau é um valor que existe. */
function stepBounds(floor: number, ceiling: number): { min: number; max: number } {
  const min = Math.floor(floor / PRICE_STEP) * PRICE_STEP;
  return { min, max: Math.max(min + PRICE_STEP, Math.ceil(ceiling / PRICE_STEP) * PRICE_STEP) };
}

export type PriceRange = {
  /** `undefined` = alça na ponta do catálogo, eixo sem corte. */
  readonly minimum?: number;
  readonly maximum?: number;
};

/**
 * A faixa de preço (a diária do hotel, a diária do carro) — duas alças e as
 * pontas editáveis em moeda, SEM histograma (chamada do designer no protótipo,
 * 2026-08-25): só o slider, como a Duração do aéreo. Alça na ponta do catálogo
 * = eixo sem corte (`undefined`).
 */
export function PriceRangeSection({
  title,
  floor,
  ceiling,
  currency,
  value,
  minimumLabel,
  maximumLabel,
  locked,
  onChange,
}: {
  readonly title: string;
  readonly floor: number;
  readonly ceiling: number;
  readonly currency: string;
  readonly value: PriceRange;
  /** Nomes acessíveis das pontas editáveis. */
  readonly minimumLabel: string;
  readonly maximumLabel: string;
  readonly locked?: boolean;
  readonly onChange: (range: PriceRange) => void;
}): ReactNode {
  const { min, max } = stepBounds(floor, ceiling);
  /* O valor controlado pode ter ficado para trás do catálogo (a lista mudou e
     o piso/teto guardados já não cabem) ou chegar invertido: as pontas são
     encaixadas ANTES de virar `Slider`, e o encaixe do `commit` faz
     o mesmo na saída. */
  const requestedFrom = Math.round(value.minimum ?? min);
  const requestedTo = Math.round(value.maximum ?? max);
  const from = Math.min(Math.max(Math.min(requestedFrom, requestedTo), min), max);
  const to = Math.min(Math.max(Math.max(requestedFrom, requestedTo), min), max);
  /* Os CAMPOS, porém, dizem o corte aplicado (#6268): a régua pode ter mudado
     sob um piso/teto guardado, e encaixar o número também no campo fazia a
     pessoa ler um corte que não é o que esconde os carros. A ponta sem corte
     acompanha a outra quando ela sai da régua, para o par não sair invertido. */
  const appliedLow = value.minimum ?? Math.min(min, value.maximum ?? min);
  const appliedHigh = value.maximum ?? Math.max(max, value.minimum ?? max);
  const fieldFrom = Math.round(Math.min(appliedLow, appliedHigh));
  const fieldTo = Math.round(Math.max(appliedLow, appliedHigh));
  const symbol = currencySymbol(currency);
  const commit = (range: readonly number[]) => {
    /* Campo editado à mão pode inverter as pontas ou sair da régua — o encaixe
       conserta os dois (`encaixar` do protótipo). */
    const low = Math.min(Math.max(Math.min(...range), min), max);
    const high = Math.min(Math.max(Math.max(...range), min), max);
    onChange({
      ...(low <= min ? {} : { minimum: low }),
      ...(high >= max ? {} : { maximum: high }),
    });
  };
  return (
    <FilterSection title={title}>
      <div className="grid gap-2">
        {/* `px-1.5` = os 6px de meio thumb: o ponteiro nasce centrado no valor e
            nas pontas sangrava pra fora do padding da coluna; `pt-1 pb-2` é a
            sangria VERTICAL do mesmo thumb sob o `panel-collapse` do bloco. */}
        <div className="grid gap-1.5 px-1.5 pt-1 pb-2">
          <Slider
            min={min}
            max={max}
            step={PRICE_STEP}
            value={[from, to]}
            aria-label={title}
            disabled={locked}
            onValueChange={(range: readonly number[]) => {
              commit(range);
            }}
          />
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <PriceField
            amount={fieldFrom}
            symbol={symbol}
            label={minimumLabel}
            disabled={locked}
            onCommit={(amount) => {
              commit([amount, to]);
            }}
          />
          <PriceField
            amount={fieldTo}
            symbol={symbol}
            label={maximumLabel}
            disabled={locked}
            onCommit={(amount) => {
              commit([from, amount]);
            }}
          />
        </div>
      </div>
    </FilterSection>
  );
}
